import { Component, Inject, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { AbstractControl, FormBuilder, FormGroup, ReactiveFormsModule, ValidationErrors, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { SpinnerVisibilityService } from 'ng-http-loader';
import { CouponService } from '../../services/coupon.service';
import { CategoryManagementService } from '../../services/category-management.service';
import { Coupon, CouponDiscountType, CouponRequest, COUPON_DISCOUNT_TYPES } from '../../model/coupon-dto';
import { AdminCategory, AdminSubcategory } from '../../model/category-management-dto';

@Component({
  selector: 'app-coupon-dialog',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatDialogModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatButtonModule,
    MatIconModule,
    MatCheckboxModule,
    MatSlideToggleModule,
    MatDatepickerModule
  ],
  templateUrl: './coupon-dialog.component.html',
  styleUrl: './coupon-dialog.component.scss'
})
export class CouponDialogComponent implements OnInit {

  form!: FormGroup;
  coupon?: Coupon;
  errorMessage: string | null = null;

  discountTypes = COUPON_DISCOUNT_TYPES;

  categories: AdminCategory[] = [];
  subcategories: AdminSubcategory[] = [];

  /**
   * Validador cruzado de rango de fechas: validFrom debe ser <= validUntil.
   * Se registra el error 'dateRange' en el control validUntil para mostrarlo
   * junto a ese campo.
   */
  private dateRangeValidator = (group: AbstractControl): ValidationErrors | null => {
    const fromControl = group.get('validFrom');
    const untilControl = group.get('validUntil');
    const from = fromControl?.value;
    const until = untilControl?.value;

    if (from && until && new Date(from).getTime() > new Date(until).getTime()) {
      untilControl?.setErrors({ ...untilControl.errors, dateRange: true });
      return { dateRange: true };
    }

    if (untilControl?.hasError('dateRange')) {
      const { dateRange, ...rest } = untilControl.errors || {};
      untilControl.setErrors(Object.keys(rest).length ? rest : null);
    }
    return null;
  };

  constructor(
    private fb: FormBuilder,
    private couponService: CouponService,
    private categoryService: CategoryManagementService,
    private spinner: SpinnerVisibilityService,
    public dialogRef: MatDialogRef<CouponDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: { coupon?: Coupon }
  ) {
    this.coupon = data.coupon;
  }

  ngOnInit(): void {
    this.form = this.fb.group({
      code: [this.coupon?.code || '', [Validators.required, Validators.maxLength(40)]],
      description: [this.coupon?.description || '', [Validators.required, Validators.maxLength(255)]],
      discountType: [this.coupon?.discountType || 'percentage', Validators.required],
      amount: [this.coupon?.amount ?? null, [Validators.required, Validators.min(0.01)]],
      maxDiscountValue: [this.coupon?.maxDiscountValue ?? null],
      maxRedemptionsTotal: [this.coupon?.maxRedemptionsTotal ?? null, [Validators.min(1)]],
      maxRedemptionsPerUser: [this.coupon?.maxRedemptionsPerUser ?? 1, [Validators.required, Validators.min(1)]],
      minServiceValue: [this.coupon?.minServiceValue ?? null],
      categoryId: [this.coupon?.categoryId || null],
      subcategoryId: [this.coupon?.subcategoryId || null],
      firstJobOnly: [this.coupon?.firstJobOnly ?? false],
      isStackable: [this.coupon?.isStackable ?? false],
      applyAutomatically: [this.coupon?.applyAutomatically ?? false],
      validFrom: [this.coupon?.validFrom ? this.parseDateOnly(this.coupon.validFrom) : null],
      validUntil: [this.coupon?.validUntil ? this.parseDateOnly(this.coupon.validUntil) : null],
      isActive: [this.coupon ? this.coupon.isActive : true]
    }, { validators: this.dateRangeValidator });

    // Ajusta las validaciones de `amount`/`maxDiscountValue` según el tipo de descuento inicial
    this.applyAmountValidators(this.form.get('discountType')?.value);

    this.form.get('discountType')?.valueChanges.subscribe((type: CouponDiscountType) => {
      this.applyAmountValidators(type);
    });

    // El código siempre se normaliza a mayúsculas mientras se escribe
    this.form.get('code')?.valueChanges.subscribe((value: string) => {
      const upper = (value || '').toUpperCase();
      if (value !== upper) {
        this.form.get('code')?.setValue(upper, { emitEvent: false });
      }
    });

    this.form.get('categoryId')?.valueChanges.subscribe((categoryId: string | null) => {
      this.updateSubcategories(categoryId);
      const subcategoryControl = this.form.get('subcategoryId');
      const stillValid = this.subcategories.some(s => s.subcategoryId === subcategoryControl?.value);
      if (!stillValid) {
        subcategoryControl?.setValue(null);
      }
    });

    this.loadCategories();
  }

  get isEdit(): boolean {
    return !!this.coupon;
  }

  get isPercentage(): boolean {
    return this.form.get('discountType')?.value === 'percentage';
  }

  private loadCategories(): void {
    this.categoryService.getCategories().subscribe({
      next: (response) => {
        this.categories = response.data || [];
        this.updateSubcategories(this.form.get('categoryId')?.value);
      },
      error: (err) => {
        console.error(err);
      }
    });
  }

  private updateSubcategories(categoryId: string | null): void {
    const category = this.categories.find(c => c.categoryId === categoryId);
    this.subcategories = category?.subcategories || [];
  }

  private applyAmountValidators(type: CouponDiscountType): void {
    const amountControl = this.form.get('amount');
    const validators = type === 'percentage'
      ? [Validators.required, Validators.min(0.01), Validators.max(100)]
      : [Validators.required, Validators.min(0.01)];
    amountControl?.setValidators(validators);
    amountControl?.updateValueAndValidity({ emitEvent: false });

    if (type !== 'percentage') {
      this.form.get('maxDiscountValue')?.setValue(null);
    }
  }

  save(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    this.errorMessage = null;
    this.spinner.show();

    const value = this.form.value;
    const body: CouponRequest = {
      code: (value.code || '').trim().toUpperCase(),
      description: value.description,
      discountType: value.discountType,
      amount: value.amount,
      maxDiscountValue: value.discountType === 'percentage' && value.maxDiscountValue ? value.maxDiscountValue : undefined,
      maxRedemptionsTotal: value.maxRedemptionsTotal || undefined,
      maxRedemptionsPerUser: value.maxRedemptionsPerUser,
      minServiceValue: value.minServiceValue || undefined,
      categoryId: value.categoryId || undefined,
      subcategoryId: value.subcategoryId || undefined,
      firstJobOnly: !!value.firstJobOnly,
      isStackable: !!value.isStackable,
      applyAutomatically: !!value.applyAutomatically,
      validFrom: value.validFrom ? this.formatDateForApi(value.validFrom) : undefined,
      validUntil: value.validUntil ? this.formatDateForApi(value.validUntil) : undefined,
      isActive: value.isActive
    };

    const request$ = this.isEdit
      ? this.couponService.updateCoupon(this.coupon!.id, body)
      : this.couponService.createCoupon(body);

    request$.subscribe({
      next: (response) => {
        this.spinner.hide();
        if (response.statusCode === '000') {
          this.dialogRef.close(true);
        } else {
          this.errorMessage = response.message || 'Error guardando el cupón';
        }
      },
      error: (err) => {
        this.spinner.hide();
        console.error(err);
        this.errorMessage = err?.error?.message || 'Error guardando el cupón';
      }
    });
  }

  /** Convierte un Date del datepicker a 'yyyy-MM-dd' en hora local (evita corrimientos por UTC) */
  private formatDateForApi(date: Date): string {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  /** Parsea 'yyyy-MM-dd' como fecha local (evita corrimientos por UTC en Bogotá, UTC-5) */
  private parseDateOnly(dateString: string): Date {
    const [year, month, day] = dateString.split('-').map(Number);
    return new Date(year, (month || 1) - 1, day || 1);
  }
}
